import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaEntry } from '../../../styles/theme';
import { formatDayMonth } from '../../../utils/dateLocale';
import type { PendingLeftover } from './useLeftoverCandidates';

interface Props {
  pending: PendingLeftover[];
  resolveTitle: (recipeId: string) => string | undefined;
}

/**
 * "2 portions de restes à placer".
 *
 * The push half of #663: fridge portions you declared but have not put anywhere
 * yet. Only the soonest-to-expire dish is named — a list of every open batch
 * would be a chore to read, and the expiring one is the only urgent one.
 *
 * Freezer portions are deliberately excluded. They keep for 90 days, so nagging
 * about them every time the planner opens would be noise rather than help.
 */
export function LeftoverPendingBanner({ pending, resolveTitle }: Props) {
  const { t, i18n } = useTranslation();
  if (pending.length === 0) return null;

  const total = pending.reduce((sum, p) => sum + p.remaining, 0);
  const soonest = pending[0];
  const recipe = resolveTitle(soonest.recipeId) ?? t('entry.subRecentMeal');

  return (
    <View style={styles.banner} testID="leftover-pending-banner">
      <View style={styles.icon}>
        <MaterialCommunityIcons name="recycle" size={17} color={PaprikaEntry.leftover.color} />
      </View>
      <View style={styles.text}>
        <Text style={styles.title}>
          {t('thisWeek.leftoverBannerTitle', { count: total })}
        </Text>
        <Text style={styles.sub} numberOfLines={1}>
          {t('thisWeek.leftoverBannerSub', {
            recipe,
            date: formatDayMonth(soonest.eatBefore, i18n.language, 'short'),
          })}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: PaprikaEntry.leftover.tint,
    borderRadius: 13,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  icon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: { flex: 1, gap: 2 },
  title: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: PaprikaEntry.leftover.color,
  },
  sub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    lineHeight: 15,
    color: Paprika.textSecondary,
  },
});
